import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  type ColumnSuggestion,
  type ExportResult,
  type ImportResult,
  type MappingTemplate,
  type PreviewResult,
  type PromoteResult,
  type Readiness,
  type VerifiedReadiness,
} from "../api.js";
import { SAMPLE_B0102, SAMPLE_CSV } from "../sample.js";
import { EntityForm } from "./EntityForm.js";
import type { JSX } from "react";

const LOGICAL_FIELDS = [
  "arrangementRef",
  "providerCode",
  "providerName",
  "providerCountry",
  "functionRef",
  "functionName",
  "startDate",
  "endDate",
  "annualCostEur",
  "cifFlag",
] as const;

const REQUIRED = new Set(["arrangementRef", "providerCode", "functionRef"]);

function parseCsvStats(raw: string): { rows: number; cols: number } | null {
  const lines = raw.split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return null;
  const headerCols = (lines[0] ?? "").split(",").length;
  return { rows: Math.max(0, lines.length - 1), cols: headerCols };
}

function ConfidenceBadge({ s }: { s: ColumnSuggestion | undefined }): JSX.Element | null {
  if (s === undefined || s.proposedField === null || s.confidence === null) return null;
  const colors: Record<string, string> = {
    high: "var(--ok, #22c55e)",
    medium: "var(--warn, #f59e0b)",
    low: "var(--bad, #ef4444)",
  };
  const labels: Record<string, string> = { high: "✓ high", medium: "~ medium", low: "? low" };
  return (
    <span
      style={{
        fontSize: "0.7rem",
        fontWeight: 600,
        color: colors[s.confidence],
        marginLeft: "4px",
        whiteSpace: "nowrap",
      }}
      title={`Confidence: ${s.confidence}`}
    >
      {labels[s.confidence]}
    </span>
  );
}

interface Props {
  registerId: string;
  onDataLoaded?: () => void;
}

export function ImportTab({ registerId, onDataLoaded }: Props): JSX.Element {
  const [csv, setCsv] = useState(SAMPLE_CSV);
  const [verifiedCsv, setVerifiedCsv] = useState(SAMPLE_B0102);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [promoteResult, setPromoteResult] = useState<PromoteResult | null>(null);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState<VerifiedReadiness | null>(null);
  const [entitySaved, setEntitySaved] = useState(false);
  const [csvDragging, setCsvDragging] = useState(false);
  const [verifiedDragging, setVerifiedDragging] = useState(false);

  // Template management
  const [savedTemplates, setSavedTemplates] = useState<MappingTemplate[]>([]);
  const [templateBannerDismissed, setTemplateBannerDismissed] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [saveTemplateError, setSaveTemplateError] = useState<string | null>(null);
  const [templateSaved, setTemplateSaved] = useState(false);

  const readFile = (file: File, setter: (v: string) => void): void => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text === "string") setter(text);
    };
    reader.readAsText(file, "utf-8");
  };

  const run = useCallback(
    (tag: string, fn: () => Promise<void>): void => {
      if (busy !== null) return;
      setBusy(tag);
      setError(null);
      fn()
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
        .finally(() => setBusy(null));
    },
    [busy],
  );

  // Load saved templates on mount
  useEffect(() => {
    api
      .listMappingTemplates()
      .then(setSavedTemplates)
      .catch(() => {
        /* silent - not critical */
      });
  }, []);

  const analyze = (): void =>
    run("analyze", async () => {
      setImportResult(null);
      setPromoteResult(null);
      setReadiness(null);
      setExportResult(null);
      setTemplateBannerDismissed(false);
      setTemplateSaved(false);
      setSaveTemplateName("");
      const p = await api.preview(csv);
      setPreview(p);
      setMapping(p.suggestedMapping);
    });

  const doImport = (): void =>
    run("import", async () => {
      setImportResult(null);
      setPromoteResult(null);
      setReadiness(null);
      setExportResult(null);
      const result = await api.importCsv(registerId, csv, mapping);
      setImportResult(result);
    });

  const promote = (): void =>
    run("promote", async () => {
      if (importResult === null) throw new Error("Import first.");
      setPromoteResult(await api.promote(registerId, importResult.batchId));
      onDataLoaded?.();
      setReadiness(null);
      setExportResult(null);
    });

  const validate = (): void =>
    run("validate", async () => {
      setReadiness(await api.validate(registerId));
    });

  const exportPkg = (): void =>
    run("export", async () => {
      setExportResult(await api.exportPackage(registerId));
    });

  const runVerified = (): void =>
    run("verified", async () => {
      const lines = verifiedCsv.split("\n");
      const template = (lines[0] ?? "B_01.02").trim();
      const body = lines.slice(1).join("\n");
      await api.importTemplates(registerId, { [template]: body });
      setVerified(await api.validateVerified(registerId));
    });

  const applyTemplate = (tpl: { name: string; bindings: Record<string, string> }): void => {
    setMapping(tpl.bindings);
    setTemplateBannerDismissed(true);
  };

  const saveTemplate = (): void => {
    const name = saveTemplateName.trim();
    if (!name) {
      setSaveTemplateError("Template name is required");
      return;
    }
    if (preview === null) return;
    setSaveTemplateError(null);
    run("save-template", async () => {
      const saved = await api.saveMappingTemplate(name, preview.headers, mapping);
      setSavedTemplates((prev) => {
        const others = prev.filter((t) => t.name !== saved.name);
        return [saved, ...others];
      });
      setTemplateSaved(true);
    });
  };

  const deleteTemplate = (name: string): void => {
    run("delete-template", async () => {
      await api.deleteMappingTemplate(name);
      setSavedTemplates((prev) => prev.filter((t) => t.name !== name));
    });
  };

  const mappingErrors = useMemo(() => [...REQUIRED].filter((f) => !mapping[f]), [mapping]);
  const importErrorCount = useMemo(
    () => importResult?.diagnostics.filter((d) => d.severity === "error").length ?? 0,
    [importResult],
  );

  const showMatchedTemplateBanner =
    preview !== null && preview.matchedTemplate !== null && !templateBannerDismissed;

  return (
    <div>
      {error !== null && (
        <div className="error" data-testid="error">
          {error}
        </div>
      )}

      <section className="card">
        <h2>1 - Your register (CSV)</h2>
        <div
          className={`drop-zone${csvDragging ? " drag-over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setCsvDragging(true);
          }}
          onDragLeave={() => setCsvDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setCsvDragging(false);
            const file = e.dataTransfer.files[0];
            if (file !== undefined) readFile(file, setCsv);
          }}
        >
          <div className="drop-zone-toolbar">
            <label className="file-btn">
              📂 Choose CSV file
              <input
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file !== undefined) readFile(file, setCsv);
                  e.target.value = "";
                }}
              />
            </label>
            <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
              or drag &amp; drop a .csv file
            </span>
          </div>
          <textarea
            data-testid="csv-input"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={8}
            spellCheck={false}
          />
          {(() => {
            const stats = parseCsvStats(csv);
            return stats !== null ? (
              <p className={`csv-stats${stats.rows > 0 ? " has-data" : ""}`}>
                {stats.rows} data rows · {stats.cols} columns
              </p>
            ) : (
              <p className="csv-stats">No data</p>
            );
          })()}
        </div>
        <div className="actions">
          <button data-testid="analyze-btn" onClick={() => void analyze()} disabled={busy !== null}>
            {busy === "analyze" ? "Analyzing..." : "Analyze columns"}
          </button>
          <button className="ghost" onClick={() => setCsv(SAMPLE_CSV)} disabled={busy !== null}>
            Reset sample
          </button>
        </div>
      </section>

      {preview !== null && (
        <section className="card" data-testid="mapping">
          <h2>2 - Map columns</h2>

          {/* Matched template banner */}
          {showMatchedTemplateBanner && (
            <div
              data-testid="template-banner"
              style={{
                background: "var(--fog)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                padding: "10px 14px",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: "0.875rem" }}>
                Saved template <strong>&quot;{preview.matchedTemplate!.name}&quot;</strong> matches
                these headers.
              </span>
              <button
                className="ghost"
                style={{ padding: "3px 10px", fontSize: "0.8rem" }}
                onClick={() => applyTemplate(preview.matchedTemplate!)}
                data-testid="apply-template-btn"
              >
                Apply template
              </button>
              <button
                className="ghost"
                style={{ padding: "3px 10px", fontSize: "0.8rem", color: "var(--muted)" }}
                onClick={() => setTemplateBannerDismissed(true)}
              >
                Dismiss
              </button>
            </div>
          )}

          <table className="mapping">
            <thead>
              <tr>
                <th>Field</th>
                <th>Source column</th>
                <th style={{ fontSize: "0.75rem", color: "var(--muted)", fontWeight: 400 }}>
                  Confidence
                </th>
              </tr>
            </thead>
            <tbody>
              {LOGICAL_FIELDS.map((field) => {
                // Find which source column is currently selected for this field
                const selectedSource = mapping[field] ?? "";
                // Get suggestion for the selected source column (or proposed one)
                const suggestion = selectedSource
                  ? preview.detailedSuggestions[selectedSource]
                  : Object.entries(preview.detailedSuggestions).find(
                      ([, s]) => s.proposedField === field,
                    )?.[1];

                return (
                  <tr key={field}>
                    <td>
                      {field}
                      {REQUIRED.has(field) ? <span className="req"> *</span> : null}
                    </td>
                    <td>
                      <select
                        data-testid={`map-${field}`}
                        value={mapping[field] ?? ""}
                        onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value }))}
                      >
                        <option value="">(unmapped)</option>
                        {preview.headers.map((h) => {
                          const s = preview.detailedSuggestions[h];
                          const isProposed = s?.proposedField === field;
                          return (
                            <option key={h} value={h}>
                              {h}
                              {isProposed && s?.confidence ? ` (${s.confidence})` : ""}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td>
                      <ConfidenceBadge s={suggestion} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Save as template */}
          <div
            style={{
              marginTop: "1rem",
              padding: "12px 14px",
              background: "var(--fog)",
              borderRadius: "8px",
              border: "1px solid var(--line)",
            }}
          >
            <p style={{ margin: "0 0 8px", fontSize: "0.82rem", color: "var(--muted)" }}>
              Save this mapping as a reusable template (auto-suggested next time these headers
              appear).
            </p>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                value={saveTemplateName}
                onChange={(e) => {
                  setSaveTemplateName(e.target.value);
                  setSaveTemplateError(null);
                  setTemplateSaved(false);
                }}
                placeholder="Template name (e.g. Acme contracts Q3)"
                data-testid="template-name-input"
                style={{
                  flex: "1 1 200px",
                  padding: "0.35rem 0.6rem",
                  border: "1px solid var(--line)",
                  borderRadius: "6px",
                  fontSize: "0.875rem",
                  background: "var(--fog)",
                  color: "var(--ink)",
                  minWidth: "160px",
                  maxWidth: "320px",
                }}
              />
              <button
                className="ghost"
                onClick={saveTemplate}
                disabled={busy !== null || saveTemplateName.trim() === ""}
                data-testid="save-template-btn"
                style={{ padding: "0.35rem 0.9rem", fontSize: "0.875rem" }}
              >
                {busy === "save-template" ? "Saving…" : "Save template"}
              </button>
              {templateSaved && (
                <span style={{ fontSize: "0.8rem", color: "var(--ok, #22c55e)" }}>Saved!</span>
              )}
              {saveTemplateError !== null && (
                <span style={{ fontSize: "0.8rem", color: "var(--bad)" }}>{saveTemplateError}</span>
              )}
            </div>

            {/* Saved templates list */}
            {savedTemplates.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <p style={{ margin: "0 0 6px", fontSize: "0.78rem", color: "var(--muted)" }}>
                  Saved templates:
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {savedTemplates.map((tpl) => (
                    <div
                      key={tpl.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        background: "var(--surface, #fff)",
                        border: "1px solid var(--line)",
                        borderRadius: "6px",
                        padding: "2px 6px 2px 10px",
                        fontSize: "0.8rem",
                      }}
                    >
                      <button
                        className="ghost"
                        style={{
                          padding: "0",
                          fontSize: "0.8rem",
                          border: "none",
                          background: "none",
                        }}
                        onClick={() => applyTemplate(tpl)}
                        title={`Apply template "${tpl.name}"`}
                        data-testid={`apply-saved-${tpl.name}`}
                      >
                        {tpl.name}
                      </button>
                      <button
                        style={{
                          padding: "0 2px",
                          fontSize: "0.75rem",
                          border: "none",
                          background: "none",
                          color: "var(--muted)",
                          cursor: "pointer",
                        }}
                        onClick={() => deleteTemplate(tpl.name)}
                        title={`Delete template "${tpl.name}"`}
                        aria-label={`Delete template ${tpl.name}`}
                        data-testid={`delete-tpl-${tpl.name}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <EntityForm registerId={registerId} onSaved={() => setEntitySaved(true)} />
            {entitySaved && null}
          </div>

          {mappingErrors.length > 0 && (
            <p className="warn">Required fields unmapped: {mappingErrors.join(", ")}</p>
          )}
          <div className="actions">
            <button
              data-testid="import-btn"
              onClick={() => void doImport()}
              disabled={busy !== null || mappingErrors.length > 0}
            >
              {busy === "import" ? "Importing..." : "Import to staging"}
            </button>
          </div>
        </section>
      )}

      {importResult !== null && (
        <section className="card" data-testid="import-result">
          <h2>3 - Staging</h2>
          <p>
            Imported <strong>{importResult.counts.arrangements}</strong> arrangements,{" "}
            <strong>{importResult.counts.providers}</strong> providers,{" "}
            <strong>{importResult.counts.functions}</strong> functions.
          </p>
          {importResult.diagnostics.length > 0 ? (
            <ul className="diagnostics" data-testid="diagnostics">
              {importResult.diagnostics.map((d, i) => (
                <li key={i} className={d.severity}>
                  {d.row !== null && <code>row {d.row}</code>} <code>{d.code}</code> {d.message}
                </li>
              ))}
            </ul>
          ) : (
            <p>No import diagnostics.</p>
          )}
          <p className="muted">
            Staged only - nothing is live yet. Promote to write these rows into the live register.
          </p>
          {importErrorCount > 0 && (
            <p className="warn" data-testid="promote-blocked">
              {importErrorCount} error-severity diagnostic(s) block promotion. Fix the source and
              re-import.
            </p>
          )}
          <div className="actions">
            <button
              data-testid="promote-btn"
              onClick={() => void promote()}
              disabled={busy !== null || importErrorCount > 0}
            >
              {busy === "promote" ? "Promoting..." : "Promote to live"}
            </button>
          </div>
        </section>
      )}

      {promoteResult !== null && promoteResult.ok && (
        <section className="card" data-testid="promote-result">
          <h2>4 - Promoted to live</h2>
          <p>
            Promoted <strong>{promoteResult.counts?.arrangements ?? 0}</strong> arrangements,{" "}
            <strong>{promoteResult.counts?.providers ?? 0}</strong> providers,{" "}
            <strong>{promoteResult.counts?.functions ?? 0}</strong> functions into the live
            register.
            {promoteResult.counts?.reconcile !== undefined && (
              <span style={{ color: "var(--muted)", marginLeft: "6px" }}>
                ({promoteResult.counts.reconcile.inserted} new,{" "}
                {promoteResult.counts.reconcile.updated} updated)
              </span>
            )}
          </p>
          <div className="actions">
            <button
              data-testid="validate-btn"
              onClick={() => void validate()}
              disabled={busy !== null}
            >
              {busy === "validate" ? "Validating..." : "Run validation"}
            </button>
          </div>
        </section>
      )}

      {readiness !== null && (
        <section className="card readiness" data-testid="readiness">
          <h2>5 - Readiness</h2>
          <div className="hero">
            <div
              className={`badge ${readiness.ready ? "ok" : "bad"}`}
              data-testid="readiness-badge"
              data-ready={readiness.ready ? "true" : "false"}
            >
              {readiness.ready ? "PASSES SAMPLE RULES" : "BLOCKED"}
            </div>
            <div className="totals">
              <span data-testid="count-blocking" className="blocking">
                {readiness.totals.blocking} blocking
              </span>
              <span data-testid="count-warning" className="warning">
                {readiness.totals.warning} warnings
              </span>
              <span className="advisory">{readiness.totals.advisory} advisory</span>
            </div>
          </div>
          <p className="muted">
            Ruleset {readiness.taxonomyVersion} ({readiness.rulesetProvenance})
          </p>
          <ul className="findings" data-testid="findings">
            {readiness.findings.map((f, i) => (
              <li key={i} className={f.severity}>
                <span className="rule">{f.ruleId}</span>
                <span className="loc">
                  {f.location.entity}
                  {f.location.ref !== null ? ` ${f.location.ref}` : ""}
                  {f.location.field !== null ? ` · ${f.location.field}` : ""}
                </span>
                <span className="msg">{f.message}</span>
              </li>
            ))}
          </ul>
          <div className="actions">
            <button
              data-testid="export-btn"
              onClick={() => void exportPkg()}
              disabled={busy !== null}
            >
              {busy === "export" ? "Building..." : "Export package"}
            </button>
          </div>
        </section>
      )}

      {exportResult !== null && (
        <section className="card" data-testid="export-result">
          <h2>6 - Package</h2>
          <p>
            Layer-1 structural check:{" "}
            <strong data-testid="layer1-ok">{exportResult.layer1.ok ? "PASS" : "FAIL"}</strong> ·
            taxonomy <code>{exportResult.manifest.taxonomyVersion}</code> ·{" "}
            {exportResult.manifest.notForSubmission ? "not for submission" : "submittable"}
          </p>
          <ul className="files">
            {exportResult.files.map((f) => (
              <li key={f.name}>
                <code>{f.name}</code> ({f.bytes} bytes)
              </li>
            ))}
          </ul>
          <a className="button" data-testid="download-zip" href={api.zipUrl(registerId)}>
            Download package (.zip)
          </a>
        </section>
      )}

      <section className="card verified-path" data-testid="verified-panel">
        <h2>Verified validation (EBA v4.0)</h2>
        <p className="muted">
          Runs the <strong>primary-source-verified</strong> EBA rules over template data (first line
          = template code, then the datapoint-code header + rows). Business rules are{" "}
          <em>warnings</em>; these are cited to the v4.0 taxonomy, not the sample ruleset. A clean
          result means the data is <strong>structurally sound against the v4.0 taxonomy</strong> —
          it is <strong>not confirmation of NCA acceptance</strong> and is not submittable as-is.
        </p>
        <div
          className={`drop-zone${verifiedDragging ? " drag-over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setVerifiedDragging(true);
          }}
          onDragLeave={() => setVerifiedDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setVerifiedDragging(false);
            const file = e.dataTransfer.files[0];
            if (file !== undefined) readFile(file, setVerifiedCsv);
          }}
        >
          <div className="drop-zone-toolbar">
            <label className="file-btn">
              📂 Choose CSV file
              <input
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file !== undefined) readFile(file, setVerifiedCsv);
                  e.target.value = "";
                }}
              />
            </label>
            <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>or drag &amp; drop</span>
          </div>
          <textarea
            data-testid="verified-input"
            value={verifiedCsv}
            onChange={(e) => setVerifiedCsv(e.target.value)}
            rows={5}
            spellCheck={false}
          />
          {(() => {
            const stats = parseCsvStats(verifiedCsv);
            return stats !== null ? (
              <p className={`csv-stats${stats.rows > 0 ? " has-data" : ""}`}>
                {stats.rows} data rows · {stats.cols} columns
              </p>
            ) : (
              <p className="csv-stats">No data</p>
            );
          })()}
        </div>
        <div className="actions">
          <button
            data-testid="verified-btn"
            onClick={() => void runVerified()}
            disabled={busy !== null}
          >
            {busy === "verified" ? "Validating..." : "Import & validate (verified)"}
          </button>
        </div>
        {verified !== null && (
          <div data-testid="verified-result">
            <div className="hero">
              <div className="badge ok" data-testid="verified-badge">
                VERIFIED · {verified.provenance}
              </div>
              <div className="totals">
                <span data-testid="verified-warnings" className="warning">
                  {verified.totals.warning} warnings
                </span>
                <span className="blocking">{verified.totals.blocking} blocking</span>
              </div>
            </div>
            <p className="muted" data-testid="verified-disclaimer">
              Structurally verified against the v4.0 taxonomy — <strong>not</strong> a submission or
              NCA acceptance decision.
            </p>
            <ul className="findings" data-testid="verified-findings">
              {verified.findings.map((f, i) => (
                <li key={i} className={f.severity}>
                  <span className="rule">{f.ruleId}</span>
                  <span className="loc">
                    {f.location.template}
                    {f.location.column !== null ? ` · ${f.location.column}` : ""} · row{" "}
                    {f.location.row}
                  </span>
                  <span className="msg">{f.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <footer className="muted">
        Keelson MVP slice · ruleset is sample/unverified · see docs/ for the founding knowledge
        base.
      </footer>
    </div>
  );
}
